import { ChartAssetData, PriceHoverData } from '@toggle/chart';
import { DisplayFormat, mapEntity, SnakeMeta } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { globalPriceFormatter } from '~/utils/asset-price/assetPrice';

import { useChartTheme } from '../../hooks/use-chart-theme/useChartTheme';
import styles from './IndicatorLegendDetails.module.scss';

const thresholdValueColors: Record<'bullish' | 'bearish', string> = {
  bullish: 'var(--viz-bullish)',
  bearish: 'var(--viz-bearish)',
};

export interface IndicatorLegendDetailsProps {
  chartAssetData: ChartAssetData;
  hoverData?: PriceHoverData;
}

export const IndicatorLegendDetails = ({
  chartAssetData,
  hoverData,
}: IndicatorLegendDetailsProps) => {
  const { t } = useTranslation('chart');
  const { colors } = useChartTheme();
  const entity = mapEntity(chartAssetData.entity);
  const snakeMeta = chartAssetData.snakeMeta as SnakeMeta;
  const threshold = snakeMeta.threshold;
  const color = colors.getThemeColor(chartAssetData.lineColorToken);

  const priceFormatter = (price: number) =>
    globalPriceFormatter({
      entity,
      price,
      priceSuffix: entity.currency,
      shouldShowCurrency: snakeMeta.display_format === DisplayFormat.CURRENCY,
    });

  const thresholdLinesData = threshold
    ? [
        {
          key: 'bullish' as const,
          value: threshold.good,
          indicatorLine: <span className={styles.positiveLine} />,
        },
        {
          key: 'bearish' as const,
          value: threshold.bad,
          indicatorLine: (
            <div className={styles.negativeIndicator}>
              <span className={styles.negativeLine} />{' '}
              <span className={styles.negativeLine} />
              <span className={styles.negativeLine} />
            </div>
          ),
        },
      ]
    : [];

  const lastClosePrice = chartAssetData.ts[chartAssetData.ts.length - 1]?.close;
  const hoveredClosePrice = hoverData?.currentPrice.close;
  const closePrice = hoveredClosePrice ?? lastClosePrice;

  return (
    <div className={styles.container} data-testid="indicator-legend-details">
      <span
        data-testid="indicator-legend-price"
        className={styles.value}
        style={{ color }}
      >
        {priceFormatter(closePrice)}
      </span>
      {thresholdLinesData
        .filter(item => typeof item.value === 'number')
        .map(item => (
          <div
            data-testid={`threshold-${item.key}`}
            key={item.key}
            className={styles.item}
          >
            {item.indicatorLine}
            <p className={styles.label}>
              {t('chart:threshold', { context: item.key })}
            </p>
            <span
              className={styles.value}
              style={{ color: thresholdValueColors[item.key] }}
            >
              {item.value}
            </span>
          </div>
        ))}
    </div>
  );
};
